import { z } from 'zod';

const baseChartSchema = z.object({
  id: z.string(),
  version: z.string().nullish(),
  title: z.string().nullish(),
  axisLabels: z.array(z.string()).nullish(),
});

const basicDotData = z.object({
  name: z.string(),
  data: z.array(z.array(z.union([z.number(), z.null()]))),
});

const seriesWithYear = z.object({
  year: z.string(),
  series: z.array(basicDotData),
});

const basicDotSeries = z.array(basicDotData);
const bubbleTimeSeries = z.array(seriesWithYear);

const dotChartSeries = z.union([basicDotSeries, bubbleTimeSeries]);

const treeMapSeries: z.ZodType = z.lazy(() =>
  z.object({
    name: z.string(),
    value: z.number(),
    children: z.array(treeMapSeries).optional(),
  })
);

const basicHeatmapSeries = z.object({
  name: z.string(),
  data: z.array(z.array(z.number())),
});

const calendarHeatmapSeries = z.object({
  name: z.string(),
  data: z.array(z.number()),
});

export const transformedXAxis = z.array(z.union([z.string(), z.number()]));
export const nullableTransformedXAxis = transformedXAxis.nullish();

const series = z.array(
  z.object({
    name: z.string(),
    data: z.array(z.union([z.number(), z.null()])),
    yAxisIndex: z.number().nullish(),
  })
);

const yAxis = z.object({
  name: z.string(),
  formatter: z.string().nullish(),
});

export const basicLineChartSchema = z.object({
  ...baseChartSchema.shape,
  chartType: z.literal('apache-line'),
  xAxis: transformedXAxis,
  yAxis: z.array(yAxis).nullish(),
  series: series,
  barData: z.array(z.string()).nullish(),
  bandData: z.array(z.array(z.string())).nullish(),
});

export const basicBarChartSchema = z.object({
  ...baseChartSchema.shape,
  chartType: z.literal('apache-bar'),
  axis: z.array(z.union([z.string(), z.number()])),
  series: series,
  isHorizontal: z.boolean().nullish(),
});

export const doughnutChartSchema = z.object({
  ...baseChartSchema.shape,
  chartType: z.literal('apache-donut'),
  xAxis: nullableTransformedXAxis,
  series: series,
});

export const dotChartSchema = z.object({
  ...baseChartSchema.shape,
  chartType: z.enum([
    'apache-axis-bubble',
    'apache-bubble',
    'apache-bubble-time',
  ]),
  series: dotChartSeries,
});

export const bumpChartSchema = z.object({
  ...baseChartSchema.shape,
  chartType: z.literal('apache-bump'),
  xAxis: transformedXAxis,
  series: series,
});

export const sankeyChartSchema = z.object({
  ...baseChartSchema.shape,
  chartType: z.literal('apache-sankey'),
  categories: z.array(z.string()),
  links: z.array(
    z.object({
      source: z.string(),
      target: z.string(),
      value: z.number(),
    })
  ),
  isVertical: z.boolean().nullish(),
});

export const treeMapChartSchema = z.object({
  ...baseChartSchema.shape,
  chartType: z.literal('apache-treemap'),
  series: z.array(treeMapSeries),
});

export const waterfallChartSchema = z.object({
  ...baseChartSchema.shape,
  chartType: z.literal('apache-waterfall'),
  xAxis: transformedXAxis,
  series: z.array(z.number()),
  seriesNames: z.array(z.string()).nullish(),
});

export const heatmapChartSchema = z.discriminatedUnion('chartType', [
  z.object({
    ...baseChartSchema.shape,
    chartType: z.literal('apache-heatmap'),
    xAxis: z.array(z.union([z.string(), z.number()])),
    yAxis: z.array(z.union([z.string(), z.number()])).nullish(),
    ranges: z.array(z.number()).nullish(),
    series: basicHeatmapSeries,
  }),
  z.object({
    ...baseChartSchema.shape,
    chartType: z.literal('apache-heatmap-calendar'),
    xAxis: z.array(z.union([z.string(), z.number()])),
    yAxis: z.array(z.union([z.string(), z.number()])).nullish(),
    ranges: z.array(z.number()).nullish(),
    series: calendarHeatmapSeries,
  }),
]);

export type TransformedXAxisType = z.infer<typeof transformedXAxis>;
export type NullableTransformedXAxisType = z.infer<
  typeof nullableTransformedXAxis
>;
